/**
 * Runtime Lottie color-swap utilities.
 *
 * The source animation (poli-bidirectional-animation_32px.json) has two sentinel colors baked in:
 *
 *   PRIMARY   sentinel → [R=1, G=0, B=0, A=1]  (pure red,   never a real design color)
 *   SECONDARY sentinel → [R=0, G=1, B=0, A=1]  (pure green, never a real design color)
 *
 * At runtime we do an exact integer match and replace these with desired brand colors.
 */

import { colors } from '~tokens/internal'

/** Parse "#RRGGBB", "RRGGBB", "#RGB", or "RGB" into a normalized [r, g, b] tuple.
 *  Returns [0, 0, 0] as a safe fallback for any invalid/unrecognized input. */
export const hexToLottieRgb = (hex: string): [number, number, number] => {
  // Strip all leading '#' characters (guards against double-hash like '##FF0000')
  const clean = hex.replace(/^#+/, '')

  // Expand 3-digit shorthand → 6-digit (e.g. "F0A" → "FF00AA")
  const expanded =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean

  // Validate: must be exactly 6 hex digits
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    console.warn(`[hexToLottieRgb] Invalid hex color: "${hex}", falling back to black.`)
    return [0, 0, 0]
  }

  const r = parseInt(expanded.slice(0, 2), 16) / 255
  const g = parseInt(expanded.slice(2, 4), 16) / 255
  const b = parseInt(expanded.slice(4, 6), 16) / 255
  return [r, g, b]
}

/**
 * Derive a lighter tint of a hex color by blending toward white.
 * amount=0 → original, amount=1 → pure white.
 */
export const tintHex = (hex: string, amount: number): string => {
  const [r, g, b] = hexToLottieRgb(hex)
  const blend = (c: number): number => c + (1 - c) * amount
  const toHex = (v: number): string =>
    Math.round(blend(v) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/** Polimorphic Staff (Assistant) brand defaults. */
export const DEFAULT_PRIMARY_HEX = colors.primary[400]
export const DEFAULT_SECONDARY_HEX = colors.secondary[400]

// Exact sentinel values baked into the JSON by bake_lottie_sentinels.py
const isPrimarySentinel = (n: number[]): boolean => n[0] === 1 && n[1] === 0 && n[2] === 0
const isSecondarySentinel = (n: number[]): boolean => n[0] === 0 && n[1] === 1 && n[2] === 0

const swapColorsInNode = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  node: any,
  primary: [number, number, number],
  secondary: [number, number, number],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  if (Array.isArray(node)) {
    if (
      node.length === 4 &&
      typeof node[0] === 'number' &&
      typeof node[1] === 'number' &&
      typeof node[2] === 'number' &&
      typeof node[3] === 'number'
    ) {
      if (isPrimarySentinel(node)) return [primary[0], primary[1], primary[2], node[3]]
      if (isSecondarySentinel(node)) return [secondary[0], secondary[1], secondary[2], node[3]]
    }
    return node.map((child) => swapColorsInNode(child, primary, secondary))
  }
  if (node !== null && typeof node === 'object') {
    const result: Record<string, unknown> = {}
    for (const key of Object.keys(node)) {
      result[key] = swapColorsInNode(node[key], primary, secondary)
    }
    return result
  }
  return node
}

/**
 * Return a deep-cloned copy of the Lottie JSON with sentinel colors replaced.
 * Both params are optional; omitting them restores the original brand palette.
 *
 * @param lottieJson   The pre-processed Lottie JSON (sentinels already injected).
 * @param primaryHex   Hex for the dark/primary dots.  Defaults to '#0E3A6A'.
 * @param secondaryHex Hex for the light/secondary dots. Defaults to a 45% tint of primaryHex.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const recolorLottie = (lottieJson: any, primaryHex?: string, secondaryHex?: string): any => {
  const resolvedPrimary = primaryHex ?? DEFAULT_PRIMARY_HEX
  const resolvedSecondary = secondaryHex ?? tintHex(resolvedPrimary, 0.45)

  return swapColorsInNode(
    lottieJson,
    hexToLottieRgb(resolvedPrimary),
    hexToLottieRgb(resolvedSecondary),
  )
}
